import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface DueDateMarkerProps {
  dueDate: string;
  orderEnd: string;
  orderStart: string;
  orderId: string;
  progress: number;
  timelineStart: Date;
  days: number;
  onMarkerClick?: (orderId: string) => void;
}

export function DueDateMarker({
  dueDate,
  orderEnd,
  orderStart,
  orderId,
  progress,
  timelineStart,
  days,
  onMarkerClick
}: DueDateMarkerProps) {
  // 計算位置（確保日期設為當天開始時間）
  const dayDiff = (start: Date, end: Date) => {
    // 將日期設為當天的開始時間以避免時差問題
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.floor((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
  };

  const dueDateObj = new Date(dueDate);
  const orderEndObj = new Date(orderEnd);
  const orderStartObj = new Date(orderStart);
  const today = new Date();
  
  const dueIdx = dayDiff(timelineStart, dueDateObj);
  
  // 如果交期不在視窗範圍內，不顯示
  if (dueIdx < 0 || dueIdx >= days) {
    return null;
  }

  // 計算狀態顏色
  const isOverdue = orderEndObj > dueDateObj;
  const isCompleted = progress === 1.0;
  
  let markerColor = '';
  let statusText = '';
  
  if (isCompleted) {
    const completedOnTime = dueDateObj >= orderEndObj;
    markerColor = '#10b981'; // 綠色 (Tailwind green-500)
    statusText = completedOnTime ? '已如期完成' : `逾期完成 (+${dayDiff(dueDateObj, orderEndObj)} 天)`;
  } else if (isOverdue) {
    markerColor = '#f97316'; // 橘色 (Tailwind orange-500)
    statusText = '排程超過交期';
  } else {
    markerColor = '#10b981'; // 綠色 (Tailwind green-500)
    statusText = '預期準時';
  }

  // 計算進度對照
  const timelineEnd = dueDateObj.getTime();
  const timelineStartTime = orderStartObj.getTime();
  const todayTime = today.getTime();
  
  const plannedProgress = Math.max(0, Math.min(1, 
    (todayTime - timelineStartTime) / (timelineEnd - timelineStartTime)
  ));
  
  const actualProgress = progress;
  const progressDiff = actualProgress - plannedProgress;
  
  const showProgressBadge = plannedProgress > 0 && plannedProgress < 1;

  const markerElement = (
    <div
      className="absolute flex flex-col items-center cursor-pointer z-20"
      style={{
        left: `${(dueIdx / days) * 100}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)'
      }}
      onClick={() => onMarkerClick?.(orderId)}
    >
      {/* 菱形標記 */}
      <div
        className={cn(
          "w-3 h-3 border-2 border-white rotate-45 transition-transform hover:scale-110"
        )}
        style={{
          backgroundColor: markerColor,
          borderColor: 'white',
        }}
      />
      
      {/* 進度對照徽章 */}
      {showProgressBadge && (
        <div className="mt-1">
          {isOverdue ? (
            <Badge 
              variant="destructive" 
              className="text-xs px-1 py-0"
            >
              <AlertTriangle className="w-2 h-2 mr-0.5" />
              超期風險
            </Badge>
          ) : progressDiff >= 0 ? (
            <Badge 
              variant="default" 
              className="text-xs px-1 py-0 bg-green-500 text-white"
            >
              <CheckCircle className="w-2 h-2 mr-0.5" />
              準時
            </Badge>
          ) : (
            <Badge 
              variant="destructive" 
              className="text-xs px-1 py-0"
            >
              <AlertTriangle className="w-2 h-2 mr-0.5" />
              落後 {Math.abs(progressDiff * 100).toFixed(0)}%
            </Badge>
          )}
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {markerElement}
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <div className="font-medium">預計交貨日</div>
            <div>{dueDateObj.toLocaleDateString('zh-TW')} ({dueDateObj.toLocaleDateString('zh-TW', { weekday: 'short' })})</div>
            <div className="text-sm text-muted-foreground">{statusText}</div>
            {showProgressBadge && (
              <div className="text-xs border-t pt-1">
                <div>時間進度: {(plannedProgress * 100).toFixed(0)}%</div>
                <div>實際進度: {(actualProgress * 100).toFixed(0)}%</div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}